import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { ExcelUploadModal } from '../../components/admin/ExcelUploadModal';
import { API_BASE } from '../../config/api';
import { StatCard } from '../../components/admin/StatCard';

export const ProgramList = () => {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUniversity, setFilterUniversity] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterPublished, setFilterPublished] = useState('all');
  const [universities, setUniversities] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [programs, filterUniversity, filterLevel, filterPublished]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Fetch programs and universities
      const [programsRes, universitiesRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/programs`, {
          headers: { 'Authorization': `Bearer ${token}` },
          cache: 'no-store' // Prevent caching
        }),
        fetch(`${backendUrl}/api/admin/universities`, {
          headers: { 'Authorization': `Bearer ${token}` },
          cache: 'no-store' // Prevent caching
        })
      ]);

      if (programsRes.status === 401 || universitiesRes.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!programsRes.ok || !universitiesRes.ok) throw new Error('Failed to fetch data');

      const programsData = await programsRes.json();
      const universitiesData = await universitiesRes.json();

      // Always set to arrays, even if empty or undefined
      const programsList = Array.isArray(programsData.programs) ? programsData.programs : [];
      const universitiesList = Array.isArray(universitiesData.universities) ? universitiesData.universities : [];
      
      setPrograms(programsList);
      setUniversities(universitiesList);
    } catch (err) {
      console.error('Error fetching data:', err);
      // Reset to empty arrays on error to ensure accurate stats
      setPrograms([]);
      setUniversities([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...programs];

    if (filterUniversity !== 'all') {
      filtered = filtered.filter(prog => prog.university_id === filterUniversity);
    }

    if (filterLevel !== 'all') {
      filtered = filtered.filter(prog => 
        prog.level_of_study && prog.level_of_study.includes(filterLevel)
      );
    }

    if (filterPublished !== 'all') {
      const isPublished = filterPublished === 'published';
      filtered = filtered.filter(prog => prog.published === isPublished);
    }

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setFilteredPrograms(filtered);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this program?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/programs/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');

      setPrograms(prev => prev.filter(prog => prog.id !== id));
    } catch (err) {
      alert('Failed to delete program');
    }
  };

  const togglePublish = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/programs/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ published: !currentStatus })
      });

      if (!response.ok) throw new Error('Failed to update');

      setPrograms(prev => 
        prev.map(prog => prog.id === id ? { ...prog, published: !currentStatus } : prog)
      );
    } catch (err) {
      alert('Failed to update publish status');
    }
  };

  const getUniversityName = (universityId) => {
    const uni = universities.find(u => u.id === universityId);
    return uni ? uni.name : 'Unknown';
  };

  // Calculate statistics - use useMemo to ensure recalculation when programs change
  const stats = useMemo(() => {
    // Ensure we're working with the latest programs array
    const currentPrograms = Array.isArray(programs) ? programs : [];
    
    const stats = {
      total: currentPrograms.length,
      byLevel: {},
      byField: {},
      published: 0,
      draft: 0
    };

    currentPrograms.forEach(prog => {
      // Skip if program is null/undefined
      if (!prog) return;

      // Count by level of study - handle both array and single value, slug or label
      if (prog.level_of_study) {
        const levels = Array.isArray(prog.level_of_study) ? prog.level_of_study : [prog.level_of_study];
        levels.forEach(level => {
          if (level) {
            // Normalize level - handle both slug and label formats
            let normalizedLevel = level;
            if (typeof level === 'string' && !level.includes('-') && level[0] === level[0].toUpperCase()) {
              // Likely a label, convert to slug
              const labelToSlug = {
                'Foundation': 'foundation',
                'Diploma': 'diploma',
                'Bachelor': 'bachelor',
                'Master': 'master',
                'PhD': 'phd',
                'Certificate': 'certificate',
                'Professional': 'professional'
              };
              normalizedLevel = labelToSlug[level] || level.toLowerCase();
            }
            stats.byLevel[normalizedLevel] = (stats.byLevel[normalizedLevel] || 0) + 1;
          }
        });
      }

      // Count by field of study
      if (prog.field_of_study) {
        const fields = Array.isArray(prog.field_of_study) ? prog.field_of_study : [prog.field_of_study];
        fields.forEach(field => {
          if (field) {
            stats.byField[field] = (stats.byField[field] || 0) + 1;
          }
        });
      }

      // Count published/draft
      if (prog.published) {
        stats.published++;
      } else {
        stats.draft++;
      }
    });

    return stats;
  }, [programs]); // Recalculate whenever programs array changes

  // Get level label from slug
  const getLevelLabel = (slug) => {
    const levelMap = {
      'foundation': 'Foundation',
      'diploma': 'Diploma',
      'bachelor': 'Bachelor',
      'master': 'Master',
      'phd': 'PhD',
      'certificate': 'Certificate',
      'professional': 'Professional'
    };
    return levelMap[slug] || slug;
  };


  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading programs...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Statistics Dashboard */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Program Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              label="Total Programs"
              value={stats.total}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }
              size="compact"
            />
            <StatCard
              label="Published"
              value={stats.published}
              iconBg="bg-emerald-100"
              iconColor="text-emerald-600"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              size="compact"
            />
            <StatCard
              label="Draft"
              value={stats.draft}
              iconBg="bg-amber-100"
              iconColor="text-amber-600"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              }
              size="compact"
            />
          </div>
        </div>

        {/* Top Levels of Study */}
        {Object.entries(stats.byLevel).length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Programs by Level of Study</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Object.entries(stats.byLevel)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([level, count], idx) => {
                  const levelLabel = getLevelLabel(level);
                  const colors = [
                    { bg: 'bg-purple-100', text: 'text-purple-600' },
                    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
                    { bg: 'bg-blue-100', text: 'text-blue-600' },
                    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
                    { bg: 'bg-teal-100', text: 'text-teal-600' }
                  ];
                  const color = colors[idx % colors.length];
                  return (
                    <StatCard
                      key={`level-${level}`}
                      label={levelLabel}
                      value={count}
                      iconBg={color.bg}
                      iconColor={color.text}
                      icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      }
                      size="compact"
                    />
                  );
                })}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2">Programs</h1>
            <p className="text-[#6B7280]">Manage program content</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-6 py-3 bg-white border-2 border-[#1E40AF] text-[#1E40AF] hover:bg-blue-50 font-semibold rounded-lg transition-all flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Bulk Import
            </button>
            <button
              onClick={() => navigate('/admin/programs/new')}
              className="px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all flex items-center gap-2"
              data-testid="add-program-button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Program
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">University</label>
              <select
                value={filterUniversity}
                onChange={(e) => setFilterUniversity(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="all">All Universities</option>
                {universities.map(uni => (
                  <option key={uni.id} value={uni.id}>{uni.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Level of Study</label>
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="all">All Levels</option>
                <option value="Foundation">Foundation</option>
                <option value="Diploma">Diploma</option>
                <option value="Bachelor">Bachelor</option>
                <option value="Master">Master</option>
                <option value="PhD">PhD</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Status</label>
              <select
                value={filterPublished}
                onChange={(e) => setFilterPublished(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-sm text-[#6B7280]">
              Showing {filteredPrograms.length} of {programs.length} programs
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl overflow-hidden">
          {filteredPrograms.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <p className="text-[#6B7280]">No programs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F8FAFC]">
                  <tr className="border-b-2 border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Program Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">University</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Level</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPrograms.map((program) => (
                    <tr
                      key={program.id}
                      className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors"
                    >
                      <td className="py-3 px-4 text-sm font-medium text-[#111827]">{program.program_name}</td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">{getUniversityName(program.university_id)}</td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">
                        {program.level_of_study && Array.isArray(program.level_of_study) ? program.level_of_study.join(', ') : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => togglePublish(program.id, program.published)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            program.published
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {program.published ? 'Published' : 'Draft'}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/admin/programs/${program.id}/edit`)}
                            className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(program.id)}
                            className="text-red-600 hover:text-red-800 font-semibold text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Excel Upload Modal */}
      <ExcelUploadModal
        entity="programs"
        entityLabel="Programs"
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          fetchData();
          setShowUploadModal(false);
        }}
      />
    </AdminLayout>
  );
};

export default ProgramList;